/**
 * CatalogueImportModal: the bulk-upload screen, on any vertical.
 * ───────────────────────────────────────────────────────────────────────────
 * Four steps: pick → review → running → done. The review step is the whole
 * point: every decision is made offline by the vertical's parser so the
 * operator sees exactly what will happen, row by row, BEFORE anything is
 * written.
 *
 * Nothing in here knows which catalogue it is filling. The vertical supplies
 * its own planner, its own runner, its own API calls and its own help text;
 * the steps, the progress bar, the review table, the run log and the
 * never-close-mid-write rule are the same work on every vertical and live
 * here once. BrandImportModal and HealthcareImportModal are both a page long
 * because of it.
 *
 * THE DEACTIVATED SWITCH IS NOT A PROP. Founder policy, 16 Sep 2026: every
 * new or imported product starts deactivated until QC activates it. The switch
 * defaults on here for every vertical, and the runner applies it; a vertical
 * cannot opt out of the default by passing something different, because there
 * is nothing to pass.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaBan,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileImport,
  FaHistory,
  FaTimesCircle,
} from "react-icons/fa";
import {
  PRODUCT_PHOTO_RULE_TEXT,
  STAND_IN_PHOTO_FILE,
} from "../../utilities/productPhotoRules";

const STEP = { PICK: "pick", REVIEW: "review", RUNNING: "running", DONE: "done" };

/* Served from public/templates beside the blank workbooks. */
const STAND_IN_PHOTO_URL = `${process.env.PUBLIC_URL || ""}/templates/${STAND_IN_PHOTO_FILE}`;

/* Deliberately NOT react-bootstrap's ProgressBar: the admin theme slims the
   .progress track, so its in-bar label is clipped into an illegible smear.
   The count and phase live OUTSIDE the track instead. */
const ImportProgress = ({ done, total, phase, label, accent }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  const phaseText =
    phase === "sections"
      ? "Creating sections"
      : phase === "products"
      ? "Uploading photos and creating products"
      : "Preparing";

  return (
    <div style={{ padding: "8px 2px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 8,
          fontSize: "0.86rem",
        }}
      >
        <strong style={{ color: accent }}>{phaseText}</strong>
        <span style={{ color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
          {done} of {total} · {pct}%
        </span>
      </div>
      <div style={{ height: 12, borderRadius: 8, background: "#e6ebf1", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 8,
            background: `linear-gradient(90deg, ${accent}, #1e9e63)`,
            transition: "width 0.35s ease",
          }}
        />
      </div>
      {label && (
        <div
          style={{
            marginTop: 8,
            fontSize: "0.8rem",
            color: "#475569",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: "0.76rem", color: "#94a3b8" }}>
        Leave this open until it finishes.
      </div>
    </div>
  );
};

const TONES = {
  ok: { bg: "#e6f4ea", fg: "#1e7e34" },
  warn: { bg: "#fff4e5", fg: "#a35b00" },
  err: { bg: "#fdecea", fg: "#b3261e" },
  mute: { bg: "#f1f5f9", fg: "#64748b" },
};

const Pill = ({ tone = "mute", children }) => (
  <span
    style={{
      background: TONES[tone].bg,
      color: TONES[tone].fg,
      borderRadius: 20,
      padding: "4px 11px",
      fontSize: "0.78rem",
      fontWeight: 600,
      marginRight: 8,
      display: "inline-block",
      marginBottom: 6,
    }}
  >
    {children}
  </span>
);

/* One font size for header and body, and no vertical rules: the house style
   for every data table on the dashboard. Bootstrap draws only horizontal
   borders unless `bordered` is set, so the rule is kept by NOT setting it. */
const TABLE_FONT_SIZE = "0.82rem";

/**
 * @param {object}   props
 * @param {boolean}  props.show
 * @param {object}   props.vendor            { _id, name }
 * @param {Array}    props.existingSections
 * @param {Function} props.onClose
 * @param {Function} props.onImported
 * @param {string}   props.accent            the action-button colour
 * @param {string}   props.title             modal heading
 * @param {string}   props.vendorNoun        the word the copy uses
 * @param {Function} props.buildPlan         (workbook, photos, ctx) => plan
 * @param {Function} props.runImport         (plan, deps) => result
 * @param {Function} [props.loadExtras]      (vendorId) => extra validation context
 * @param {Function} props.onDownloadTemplate ({ extras, existingSections }) => void
 * @param {string}   props.templateLabel
 * @param {object}   props.api               { createSection, fetchSections, createProduct, uploadImage }
 * @param {node}     props.pickHelp          what to tell the operator before they pick
 * @param {Array}    [props.extraColumns]    [{ header, width, cell(item) }]
 */
const CatalogueImportModal = ({
  show,
  vendor,
  existingSections,
  onClose,
  onImported,
  accent,
  title,
  vendorNoun,
  buildPlan,
  runImport,
  loadExtras,
  onDownloadTemplate,
  templateLabel,
  api,
  pickHelp,
  extraColumns = [],
}) => {
  const [step, setStep] = useState(STEP.PICK);
  const [workbook, setWorkbook] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [plan, setPlan] = useState(null);
  const [parseError, setParseError] = useState("");
  const [validating, setValidating] = useState(false);
  const [importDisabled, setImportDisabled] = useState(true);
  const [progress, setProgress] = useState({ done: 0, total: 0, phase: "", label: "" });
  const [result, setResult] = useState(null);

  /* The platform category list, the unit list and whatever else the vertical
     needs. All of them only produce WARNINGS, never errors, so a failure to
     load any of them degrades the review rather than blocking the import. */
  const [ctxExtras, setCtxExtras] = useState({ platformCategories: [], units: [] });

  const workbookRef = useRef(null);
  const photosRef = useRef(null);
  const vendorId = vendor?._id;

  useEffect(() => {
    if (!show || !vendorId || !loadExtras) return undefined;
    let cancelled = false;

    (async () => {
      const extras = await loadExtras(vendorId);
      if (cancelled) return;
      setCtxExtras(extras);
    })();

    return () => {
      cancelled = true;
    };
  }, [show, vendorId, loadExtras]);

  const reset = () => {
    setStep(STEP.PICK);
    setWorkbook(null);
    setPhotos([]);
    setPlan(null);
    setParseError("");
    setValidating(false);
    setImportDisabled(true);
    setProgress({ done: 0, total: 0, phase: "", label: "" });
    setResult(null);
    if (workbookRef.current) workbookRef.current.value = "";
    if (photosRef.current) photosRef.current.value = "";
  };

  const handleClose = () => {
    // Never close mid-write: half the catalogue would be in an unknown state
    // and the log — the only record of which rows landed — would be gone.
    if (step === STEP.RUNNING) return;
    const imported = result?.created?.products > 0;
    reset();
    onClose();
    if (imported) onImported();
  };

  const handleValidate = async () => {
    if (!workbook) return;
    setValidating(true);
    setParseError("");
    try {
      const built = await buildPlan(workbook, photos, {
        existingSections: existingSections || [],
        ...ctxExtras,
      });
      setPlan(built);
      setStep(STEP.REVIEW);
    } catch (err) {
      setParseError(err.message || "That workbook could not be read.");
    } finally {
      setValidating(false);
    }
  };

  const handleRun = async () => {
    setStep(STEP.RUNNING);
    try {
      const res = await runImport(plan, {
        vendor,
        createSection: api.createSection,
        fetchSections: api.fetchSections,
        createProduct: api.createProduct,
        uploadImage: api.uploadImage,
        forceDisabled: importDisabled,
        onProgress: setProgress,
      });
      setResult(res);
      setStep(STEP.DONE);
      if (res.created.products > 0) {
        toast.success(`Imported ${res.created.products} products`);
      } else {
        toast.error("Nothing was imported. See the log below.");
      }
    } catch (err) {
      // The runner is built never to throw for a row-level problem, so this is
      // a programming error or a dead network. Land on DONE anyway — the
      // operator still needs the log.
      setResult({
        created: { sections: 0, products: 0, images: 0 },
        failed: { sections: 0, products: 0 },
        log: [{ level: "error", message: err.message || "The import stopped unexpectedly." }],
      });
      setStep(STEP.DONE);
    }
  };

  const photoSummary = useMemo(() => {
    if (!photos.length) return "";
    const mb = photos.reduce((n, f) => n + (f.size || 0), 0) / (1024 * 1024);
    return `${photos.length} photos (${mb.toFixed(1)} MB)`;
  }, [photos]);

  const t = plan?.totals;

  const statusCell = (it) => {
    if (it.skipped) {
      return (
        <span style={{ color: TONES.mute.fg }}>
          {it.duplicateOf ? <FaHistory /> : <FaBan />} {it.skipReason}
        </span>
      );
    }
    if (!it.ok) {
      return (
        <span style={{ color: TONES.err.fg }}>
          <FaTimesCircle /> {it.errors.join("; ")}
        </span>
      );
    }
    if (it.warnings.length) {
      return (
        <span style={{ color: TONES.warn.fg }}>
          <FaExclamationTriangle /> {it.warnings.join("; ")}
        </span>
      );
    }
    return (
      <span style={{ color: TONES.ok.fg }}>
        <FaCheckCircle /> Ready
      </span>
    );
  };

  const actionButtonStyle = { background: accent, border: "none" };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static" centered>
      <Modal.Header closeButton={step !== STEP.RUNNING}>
        <Modal.Title>
          <FaFileImport style={{ marginRight: 10, color: accent }} />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto" }}>
        {step === STEP.PICK && (
          <>
            <div
              style={{
                background: "#eef4fb",
                border: "1px solid #cfe0f2",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 18,
                fontSize: "0.86rem",
                color: accent,
              }}
            >
              {/* Built here rather than served as a static file, so it comes
                  out carrying THIS vendor's existing section titles spelled
                  the way the importer will match them. */}
              <button
                type="button"
                onClick={() =>
                  onDownloadTemplate({
                    extras: ctxExtras,
                    existingSections: existingSections || [],
                  })
                }
                style={{
                  fontWeight: 600,
                  color: accent,
                  background: "none",
                  border: "none",
                  padding: 0,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                <FaDownload style={{ marginRight: 6 }} />
                {templateLabel}
              </button>
              <div style={{ marginTop: 6, lineHeight: 1.45 }}>{pickHelp}</div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Workbook (.xlsx)</Form.Label>
              <Form.Control
                ref={workbookRef}
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setWorkbook(e.target.files[0] || null)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>
                Product photos <span style={{ fontWeight: 400, color: "#64748b" }}>(optional)</span>
              </Form.Label>
              <Form.Control
                ref={photosRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPhotos(Array.from(e.target.files || []))}
              />
              <Form.Text muted>
                Each photo: {PRODUCT_PHOTO_RULE_TEXT}. Skip these only if every row
                carries an Image URL. A product cannot be created without a cover
                image. {photoSummary}
              </Form.Text>
              {/* Lusuku's own stand-in, for rows still waiting on photography.
                  Name it on the READ ME's "Stand-in cover file" setting and
                  those rows come in marked "Needs photo". */}
              <a
                href={STAND_IN_PHOTO_URL}
                download={STAND_IN_PHOTO_FILE}
                style={{ display: "inline-block", marginTop: 4, fontSize: "0.82rem", fontWeight: 600, color: accent }}
              >
                <FaDownload style={{ marginRight: 6 }} />
                Lusuku's stand-in photo, for products still waiting on photography
              </a>
            </Form.Group>

            {parseError && (
              <Alert variant="danger" style={{ fontSize: "0.88rem" }}>
                {parseError}
              </Alert>
            )}
          </>
        )}

        {step === STEP.REVIEW && plan && (
          <>
            <div style={{ marginBottom: 6 }}>
              <Pill tone="ok">{t.itemsOk} ready</Pill>
              {t.itemsFailed > 0 && <Pill tone="err">{t.itemsFailed} cannot import</Pill>}
              {t.itemsWithWarnings > 0 && <Pill tone="warn">{t.itemsWithWarnings} with warnings</Pill>}
              {t.itemsOutOfStock > 0 && <Pill tone="mute">{t.itemsOutOfStock} out of stock</Pill>}
              {t.itemsAlreadyImported > 0 && (
                <Pill tone="mute">{t.itemsAlreadyImported} already imported</Pill>
              )}
              {t.itemsPlaceholderCover > 0 && (
                <Pill tone="warn">{t.itemsPlaceholderCover} placeholder covers</Pill>
              )}
              <Pill tone="mute">
                {t.sectionsToCreate} new section{t.sectionsToCreate === 1 ? "" : "s"}
              </Pill>
              {t.sectionsExisting > 0 && <Pill tone="mute">{t.sectionsExisting} existing</Pill>}
              {t.imagesToUpload > 0 && <Pill tone="mute">{t.imagesToUpload} photos to upload</Pill>}
            </div>

            <div style={{ fontSize: "0.78rem", color: "#64748b", marginBottom: 12, lineHeight: 1.5 }}>
              Read from <strong>{plan.sheetsUsed.products}</strong>
              {plan.sheetsUsed.sections ? ` and ${plan.sheetsUsed.sections}` : ""}
              {plan.specColumns.length
                ? ` · specs: ${plan.specColumns.map((c) => c.fieldKey).join(", ")}`
                : ""}
              . Commission and country are calculated by the server and are never sent
              from here.
            </div>

            {!plan.hasAvailabilityColumn && (
              <Alert variant="warning" style={{ fontSize: "0.84rem", padding: "8px 12px" }}>
                This workbook has no <code>availability</code> column, so the in-stock
                filter did not run and every valid row will be imported.
              </Alert>
            )}

            {t.itemsPlaceholderCover > 0 && (
              <Alert variant="warning" style={{ fontSize: "0.84rem", padding: "8px 12px" }}>
                {t.itemsPlaceholderCover} product
                {t.itemsPlaceholderCover === 1 ? " is" : "s are"} coming in with a
                stand-in cover image. They will be marked <strong>Needs photo</strong> on
                the product page, and you can filter for them there when the real
                photographs arrive.
              </Alert>
            )}

            {plan.unusedFiles.length > 0 && (
              <Alert variant="warning" style={{ fontSize: "0.84rem", padding: "8px 12px" }}>
                {plan.unusedFiles.length} picked photo
                {plan.unusedFiles.length === 1 ? " matches no row" : "s match no row"} and
                will not be uploaded: {plan.unusedFiles.slice(0, 6).join(", ")}
                {plan.unusedFiles.length > 6 ? "…" : ""}
              </Alert>
            )}

            <Form.Check
              type="switch"
              id={`catalogue-import-disabled-${vendorNoun}`}
              checked={importDisabled}
              onChange={(e) => setImportDisabled(e.target.checked)}
              label={
                <span style={{ fontSize: "0.88rem", textTransform: "none" }}>
                  <strong>Import everything deactivated</strong>. Products are created
                  hidden and you activate them once prices and photos are
                  confirmed. Leave this on unless the catalogue has already been
                  checked.
                </span>
              }
              style={{ marginBottom: 14, textTransform: "none" }}
            />

            <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <Table size="sm" hover style={{ marginBottom: 0, fontSize: TABLE_FONT_SIZE }}>
                <thead style={{ position: "sticky", top: 0, background: "#f7f9fb", zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 44, fontSize: TABLE_FONT_SIZE }}>Row</th>
                    <th style={{ fontSize: TABLE_FONT_SIZE }}>Product</th>
                    <th style={{ width: 130, fontSize: TABLE_FONT_SIZE }}>Section</th>
                    {extraColumns.map((c) => (
                      <th key={c.header} style={{ width: c.width, fontSize: TABLE_FONT_SIZE }}>
                        {c.header}
                      </th>
                    ))}
                    <th style={{ width: 100, fontSize: TABLE_FONT_SIZE }}>Price</th>
                    <th style={{ width: 78, fontSize: TABLE_FONT_SIZE }}>Photo</th>
                    <th style={{ fontSize: TABLE_FONT_SIZE }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.items.map((it) => (
                    <tr
                      key={it.sourceRow}
                      style={{
                        background: it.skipped
                          ? "#f8fafc"
                          : it.ok
                          ? undefined
                          : "#fdf3f2",
                        opacity: it.skipped ? 0.72 : 1,
                      }}
                    >
                      <td>{it.sourceRow}</td>
                      <td style={{ fontWeight: 600 }}>{it.name || <em>(no name)</em>}</td>
                      <td>{it.sectionTitle || "—"}</td>
                      {extraColumns.map((c) => (
                        <td key={c.header}>{c.cell(it)}</td>
                      ))}
                      <td>
                        {it.priceOnRequest
                          ? "On request"
                          : it.price === null
                          ? "—"
                          : it.price.toLocaleString()}
                      </td>
                      <td>
                        <Badge
                          bg={
                            it.cover.kind === "file"
                              ? "secondary"
                              : it.cover.kind === "url"
                              ? "info"
                              : "danger"
                          }
                        >
                          {it.cover.kind === "file"
                            ? "file"
                            : it.cover.kind === "url"
                            ? "url"
                            : "none"}
                        </Badge>
                      </td>
                      <td>{statusCell(it)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {step === STEP.RUNNING && <ImportProgress {...progress} accent={accent} />}

        {step === STEP.DONE && result && (
          <>
            <div style={{ marginBottom: 10 }}>
              <Pill tone="ok">{result.created.products} products created</Pill>
              {result.created.sections > 0 && (
                <Pill tone="ok">{result.created.sections} sections created</Pill>
              )}
              {result.created.images > 0 && (
                <Pill tone="mute">{result.created.images} photos uploaded</Pill>
              )}
              {result.failed.products > 0 && (
                <Pill tone="err">{result.failed.products} products failed</Pill>
              )}
              {result.failed.sections > 0 && (
                <Pill tone="err">{result.failed.sections} sections failed</Pill>
              )}
            </div>

            {importDisabled && result.created.products > 0 && (
              <Alert variant="info" style={{ fontSize: "0.85rem", padding: "9px 13px" }}>
                All {result.created.products} products were created{" "}
                <strong>deactivated</strong>. Activate them from this page, per product or a
                whole section at a time, once you have confirmed prices and photos.
              </Alert>
            )}

            <div
              style={{
                maxHeight: 320,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: TABLE_FONT_SIZE,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {result.log.map((line, i) => (
                <div
                  key={i}
                  style={{
                    color:
                      line.level === "error"
                        ? TONES.err.fg
                        : line.level === "warn"
                        ? TONES.warn.fg
                        : TONES.ok.fg,
                    padding: "2px 0",
                  }}
                >
                  {line.message}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === STEP.PICK && (
          <>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button style={actionButtonStyle} disabled={!workbook || validating} onClick={handleValidate}>
              {validating ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Checking…
                </>
              ) : (
                "Check the workbook"
              )}
            </Button>
          </>
        )}

        {step === STEP.REVIEW && (
          <>
            <Button variant="outline-secondary" onClick={() => setStep(STEP.PICK)}>
              Back
            </Button>
            <Button style={actionButtonStyle} disabled={!plan?.canCommit} onClick={handleRun}>
              Import {t?.itemsOk || 0} product{t?.itemsOk === 1 ? "" : "s"}
            </Button>
          </>
        )}

        {step === STEP.RUNNING && (
          <Button variant="outline-secondary" disabled>
            Importing…
          </Button>
        )}

        {step === STEP.DONE && (
          <Button style={actionButtonStyle} onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CatalogueImportModal;
